import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, MapPin, ArrowRight, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../../data/site";
import { Reveal } from "./Reveal";
import { useEstimate } from "./EstimateDialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", message: "" };

export const Contact = () => {
  const { open } = useEstimate();
  const [form, setForm] = useState(empty);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Please add your name, email and phone."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setDone(true); setForm(empty);
      toast.success("Message sent! We'll be in touch shortly.");
    } catch { toast.error("Something went wrong. Please call us instead."); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="bg-white py-24 lg:py-32" data-testid="contact">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-[28px] bg-ffc-ink px-6 py-16 text-center grain sm:px-12 lg:py-20">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-ffc-blue/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-ffc-red/30 blur-3xl" />
            <div className="relative">
              <h2 className="font-display mx-auto max-w-3xl text-4xl font-extrabold text-white sm:text-6xl">
                Let&apos;s build something great together.
              </h2>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                <a href={CONTACT.phoneHref} data-testid="contact-call-btn"
                  className="btn-outline-dark inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold">
                  <Phone className="h-5 w-5" /> Call Now
                </a>
                <button onClick={open} data-testid="contact-estimate-btn"
                  className="btn-red inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold hover:bg-ffc-red">
                  Request Free Estimate <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-2">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-ffc-red">Get In Touch</span>
            <h3 className="font-display mt-3 text-3xl font-extrabold sm:text-4xl">Contact Fast Fix Charlotte.</h3>
            <p className="mt-4 max-w-md text-ffc-gray">
              Call, email or send us a message — we respond within one business day.
            </p>
            <div className="mt-8 space-y-5">
              {[
                { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref },
                { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref },
                { icon: Clock, label: "Business Hours", value: CONTACT.hours },
                { icon: MapPin, label: "Location", value: CONTACT.city },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ffc-light text-ffc-blue">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-ffc-gray">{c.label}</div>
                    {c.href
                      ? <a href={c.href} className="font-display text-lg font-bold text-ffc-ink hover:text-ffc-blue">{c.value}</a>
                      : <div className="font-display text-lg font-bold text-ffc-ink">{c.value}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6 }} className="rounded-2xl border border-ffc-line bg-ffc-light p-7 sm:p-9">
            {done ? (
              <div className="flex h-full min-h-[380px] flex-col items-center justify-center text-center" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-ffc-blue" />
                <h4 className="font-display mt-5 text-2xl font-bold">Thanks for reaching out!</h4>
                <p className="mt-2 text-ffc-gray">We&apos;ve received your message and will be in touch shortly.</p>
                <button onClick={() => setDone(false)} className="mt-6 font-semibold text-ffc-blue">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="c-name">Full name</Label>
                    <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" className="mt-1.5 bg-white" />
                  </div>
                  <div>
                    <Label htmlFor="c-phone">Phone</Label>
                    <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(704) 201-4261" className="mt-1.5 bg-white" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" className="mt-1.5 bg-white" />
                </div>
                <div>
                  <Label htmlFor="c-msg">Project details</Label>
                  <Textarea id="c-msg" data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={5} placeholder="Tell us about your project..." className="mt-1.5 bg-white" />
                </div>
                <Button type="submit" disabled={loading} data-testid="contact-submit"
                  className="btn-red h-12 w-full rounded-full text-base font-semibold hover:bg-ffc-red">
                  {loading ? "Sending..." : <>Request Free Estimate <Send className="ml-2 h-4 w-4" /></>}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
